<template>
  <div class="modal-container">
    <div class="wraper-content">
      <div class="wraper-title">
        <h1 class="header">Reset Your Password</h1>
        <h3 class="text-modal">
          We have sent a verification code to your phone.
        </h3>
      </div>
      <form
        class="form-input"
        @submit.prevent="formSubmit"
        @input="formChanged"
      >
        <InputEmail
          v-model.trim="email"
          v-focus
          v-validate="'required|email|max:128'"
          class=""
          name="login"
          type="email"
          :label="'Email'"
          :tabindex="1"
          :placeholder="'your@email.com'"
          autofocus
          autocomplete-off
          :error="errors.first($$t('Email', 'email-label'))"
        />

        <invisible-recaptcha
          class="new-signup-btn"
          :sitekey="recaptchaKey"
          :validate="() => formSubmit"
          :tabindex="2"
          :callback="formSubmit"
          :loading="isLoading"
          >Reset Password
        </invisible-recaptcha>

        <button
          :tabindex="3"
          class="new-signup-btn back-signin"
          @click="openSignInModal"
        >
          Back to sign in
        </button>
      </form>
    </div>
    <ModalBackdrop
      v-model="isLoading"
      class="password-reset-loader"
      :style="{ width: `100%` }"
      :click_on_backdrop="false"
      :esc_key="false"
      :variant="'loader'"
    >
      <img
        src="~@/assets/images/playhera-indo/Playhera_Loader.svg"
        alt="Loader"
      />
    </ModalBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { translate } from '@/mixins/translate';

import { RECAPTCHA_KEY } from '@/api/config';

import InputEmail from '@/components/form/NewInputEmail';

import isLoading from '@/mixins/isLoading';

import InvisibleRecaptcha from 'vue-invisible-recaptcha';
import ModalBackdrop from '@/components/common/ModalBackdrop.vue';
// import HeraLogo from '@/components/atoms/HeraLogo';
// import BackBtn from '@/components/common/GoBackBtn';

let routerNameFrom;
export default {
  name: 'PasswordResetModal',
  components: {
    InputEmail,
    ModalBackdrop,
    'invisible-recaptcha': InvisibleRecaptcha,
  },
  mixins: [isLoading, translate('_web_forgotpassword')],
  inject: ['$validator'],
  data() {
    return {
      email: null,
      recaptchaKey: RECAPTCHA_KEY,
    };
  },

  computed: {
    ...mapGetters(['templateRegData']),
  },

  created() {
    this.$options.verifyCodeCount = 4;
  },
  mounted() {
    this.errors.clear();
    if (routerNameFrom !== 'verifyCode') {
      this.saveRegData(null);

      const { templateRegData } = this;
      if (templateRegData) {
        this.email = templateRegData.email || null;
      }
    }
  },
  methods: {
    ...mapMutations(['saveRegData']),
    ...mapActions(['requestPass', 'errorNotify', 'openModal', 'closeModal']),

    handleRecaptchaResponse(captcha_response) {
      this.recaptchaResponse = captcha_response;
      this.formSubmit();
    },
    openSignInModal() {
      this.openModal({
        name: 'SignInModal',
        settings: {
          clickOnBackdrop: false,
          escKey: false,
          variant: 'transparent',
        },
      });
    },

    formSubmit(token) {
      const captcha_response = token;
      if (!this.errors.count()) {
        this.setLoading();
        this.isLoading = true;

        this.$validator
          .validateAll()
          .then(result =>
            result
              ? Promise.resolve(this.email)
              : Promise.reject('Invalid login')
          )
          .then(login => {
            const payload = {
              login,
              captcha_response: captcha_response,
            };
            return this.$store.dispatch('requestPass', payload);
          })
          .then(res => {
            this.isLoading = false;
            this.openModal({
              name: 'CodeVerifyModal',
              settings: {
                variant: 'transparent',
                escKey: false,
                clickOnBackdrop: false,
              },
              props: {
                email: this.email,
                tokenToSend: res.temporary_token,
                from: 'password-reset',
              },
            });
          })
          .catch(this.errorNotify)
          .finally(this.unsetLoading(), (this.isLoading = false));
      }
    },

    formChanged() {
      this.errors.clear();
      this.$validator.validate('login');
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      this.errors.clear();
    },
  },
};
</script>
<style lang="scss" scoped>
.modal-container {
  border-radius: 16px;
  background: #151520 !important;
  max-height: 650px;
  flex-direction: column;
  align-items: center;
  min-height: 60dvh;
  max-height: 90dvh;
  display: flex;
  gap: 32px;
  flex-direction: column;
  justify-content: center;
  font-family: 'Alexandria', sans-serif;
  max-width: 625px;
  min-width: 625px;
  @media (max-width: 915px) {
    max-width: 525px;
    min-width: 525px;
  }
  @media (max-width: 600px) {
    max-width: 455px;
    min-width: 455px;
  }
  @media (max-width: 450px) {
    max-width: 325px;
    min-width: 325px;
    overflow-y: auto;
  }
  @media (max-height: 600px) {
    overflow-y: scroll;
    justify-content: flex-start;
  }
  > .wraper-content {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    gap: 32px;
    height: 100%;
    min-height: 450px;
    padding: 40px 32px;
    @media (max-width: 600px) {
      padding: 40px 20px;
    }
    > .wraper-title {
      display: flex;
      flex-direction: column;
      gap: 4px;

      > .header {
        margin: 0 !important;
        font-size: 32px;
        font-style: normal;
        font-weight: 600;
        line-height: 135%; /* 43.2px */
        letter-spacing: -0.48px;
        background: var(
          --Title,
          linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.5) 5.29%,
            rgba(255, 255, 255, 0.91) 51.55%,
            rgba(255, 255, 255, 0.5) 100%
          )
        );
        background-clip: content-box;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      > .text-modal {
        margin: 0 !important;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        color: rgba(255, 255, 255, 0.5);
        text-align: center;
        > a {
          color: var(--opacity-700, rgba(255, 255, 255, 0.8));
          cursor: pointer;
          &:hover {
            color: #5a58f2;
          }
        }
      }
    }
    > form.form-input {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      align-self: stretch;
      width: 100%;

      > .new-signup-btn {
        background-color: white !important;
        display: flex;
        padding: 16px;
        justify-content: center;
        align-items: center;
        gap: 12px;
        align-self: stretch;
        color: #121217 !important;
        text-align: center;
        /* Body/Body-16/Medium */
        font-family: 'Alexandria';
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
        margin-top: 10px;
      }
      > .back-signin {
        background-color: black !important;

        color: white !important;
      }
    }
  }
  .password-reset-loader {
    width: 100vw !important;
    > .modalbackdrop_container {
      background: none !important;
      background-color: transparent !important;
    }
  }
}
</style>
